import React, { useEffect, useState, useRef, useCallback } from "react";
import MetaTags from "react-meta-tags";
import {
    Card,
    CardBody,
    Col,
    Container,
    Row
} from "reactstrap";
//Import Breadcrumb
import Breadcrumbs from "components/Common/Breadcrumb";
import Datatable from "components/Datatables/datatable";
import { post } from "helpers/api_helper";
import "react-datepicker/dist/react-datepicker.css";
import { Filters } from "../filters";
import Maincard from "../components/maincard";
import moment from 'moment';
import { runBatchLoader } from "utils/batchLoader";

const isType = 'Orderforms'

const Orderforms = () => {
    const [rowData, setRowData] = useState(null)
    const [filterData, setFilterData] = useState(null)
    const [isLoading, setIsLoading] = useState(true)
    const [pagination, setPagination] = useState({
        limit: 10,
        page: 1,
        search: ''
    })
    const [batchProgress, setBatchProgress] = useState(null)
    const batchCancelRef = useRef(false)
    const isBatchRunningRef = useRef(false)
    const totalRowsRef = useRef(0)
    const lastQueryRef = useRef('')

    const [counts, setCounts] = useState([
        { title: "Total Orders", key: "new", value: 0 },
        { title: "Total Clients", key: "redo", value: 0 },
        { title: "Total Orderform Value", key: "correction", value: 0 },
        { title: "Total Orderform Value With GST", key: "total", value: 0 },
    ]);

    useEffect(() => {
        if (rowData?.total !== undefined && rowData?.total !== null) {
            totalRowsRef.current = rowData.total;
        }
    }, [rowData]);

    const handleCancelBatch = useCallback(() => {
        batchCancelRef.current = true;
        isBatchRunningRef.current = false;
        setBatchProgress(prev => prev ? ({ ...prev, active: false, stopped: true }) : null);
        setIsLoading(false);
    }, []);

    const handlePaginationChange = useCallback((newPag) => {
        setPagination(prev => {
            if (
                prev.page === newPag.page &&
                prev.limit === newPag.limit &&
                prev.search === newPag.search &&
                Boolean(prev.isAll) === Boolean(newPag.isAll)
            ) {
                return prev;
            }
            return { ...prev, ...newPag };
        });
    }, []);

    const handleFilterChange = useCallback((filters) => {
        setFilterData(filters);
        setPagination(prev => ({ ...prev, page: 1 }));
    }, []);

    // table Column Data
    let tableColumns = [
        {
            dataField: 'id',
            text: '#',
            sort: true
        },{
            dataField: 'orderform_number',
            text: 'Orderform Number',
            sort: true
        },{
            dataField: 'order_number',
            text: 'Order No',
            sort: true,
        },{
            dataField: 'client',
            text: 'Client',
            sort: true,
            formatter: (cell, row) => `${row.clientname || ''} (${row.client || ''})`
        },{
            dataField: 'orderform_date',
            text: 'Orderform Date',
            sort: true,
            formatter: (cell, row) => row.orderform_date ? moment(row.orderform_date).format('DD-MM-YYYY') : '-'
        },{
            dataField: 'order_value',
            text: 'Sub Total (₹)',
            sort: true,
            formatter: (cell, row) => <span>₹ {parseFloat(row.order_value || 0).toFixed(2)}</span>,
            csvFormatter: (cell, row) => parseFloat(row.order_value || 0).toFixed(2)
        },{
            dataField: 'order_value_gst',
            text: 'Total Value With GST (₹)',
            sort: true,
            dummyField: true,
            formatter: (cell, row) => <b>₹ {(parseFloat(row.order_value || 0) * 1.05).toFixed(2)}</b>,
            csvFormatter: (cell, row) => (parseFloat(row.order_value || 0) * 1.05).toFixed(2)
        }
    ];

    const loadOrderforms = useCallback(async () => {
        if (!filterData || Object.keys(filterData).length === 0) return;

        const isAllSelected = Boolean(
            pagination?.isAll ||
            pagination?.limit === 'all' ||
            pagination?.limit === 'All' ||
            (typeof pagination?.limit === 'number' && pagination?.limit > 100)
        );

        if (isAllSelected) {
            lastQueryRef.current = '';
            batchCancelRef.current = true;
            await new Promise(r => setTimeout(r, 60));
            batchCancelRef.current = false;
            isBatchRunningRef.current = true;

            setIsLoading(true);
            try {
                await runBatchLoader({
                    fetchPage: async (page, batchSize) => {
                        const payload = {
                            ...filterData,
                            page,
                            limit: batchSize,
                            searchTerm: pagination?.search || ''
                        };
                        const res = await post('api/reports/orderforms', payload);
                        return res?.status === 200 ? res.response : {};
                    },
                    batchSize: 100,
                    initialTotal: totalRowsRef.current || 0,
                    onProgress: setBatchProgress,
                    onRowsLoaded: (accumulatedRows, totalCount) => {
                        totalRowsRef.current = totalCount;
                        setRowData(prev => ({
                            ...(prev || {}),
                            total: totalCount,
                            data: accumulatedRows
                        }));
                    },
                    cancelRef: batchCancelRef,
                    runningRef: isBatchRunningRef,
                });
            } catch (err) {
                console.error("Error batch loading orderforms:", err);
            } finally {
                setIsLoading(false);
            }
        } else {
            const payload = {
                ...filterData,
                page: pagination.page,
                limit: pagination.limit,
                searchTerm: pagination?.search || ''
            };
            const requestKey = `${JSON.stringify(payload)}`;
            if (lastQueryRef.current === requestKey) return;
            lastQueryRef.current = requestKey;

            setIsLoading(true);
            setBatchProgress(null);
            try {
                const res = await post('api/reports/orderforms', payload);
                if (res?.status === 200) {
                    setRowData(res.response);
                    if (res.response?.total !== undefined) {
                        totalRowsRef.current = res.response.total;
                    }
                }
            } catch (err) {
                console.error("Error loading orderforms:", err);
            } finally {
                setIsLoading(false);
            }
        }
    }, [filterData, pagination]);

    useEffect(() => {
        loadOrderforms();
    }, [loadOrderforms]);

    useEffect(() => {
        if (!rowData) return;

        const totalOrders = rowData?.total !== undefined ? rowData.total : (rowData?.data?.length || 0);
        const totalClients = rowData?.total_clients !== undefined
            ? rowData.total_clients
            : (new Set(rowData?.data?.map(d => d.clientname || d.client)).size || 0);
        const orderformvalue = rowData?.orderformvalue !== undefined
            ? rowData.orderformvalue
            : (rowData?.data?.reduce((sum, item) => sum + (parseFloat(item.order_value) || 0), 0) || 0);

        setCounts([
            { title: "Total Orders", key: "new", value: totalOrders },
            { title: "Total Clients", key: "redo", value: totalClients },
            { title: "Total Orderform Value", key: "correction", value: Number(orderformvalue).toLocaleString('en-IN', { style: 'currency', currency: 'INR' }) },
            { title: "Total Orderform Value With GST", key: "total", value: (orderformvalue * 1.05).toLocaleString('en-IN', { style: 'currency', currency: 'INR' }) },
        ]);
    }, [rowData]);

    return (
        <React.Fragment>
            <div className="page-content">
                <MetaTags> <title>Dashboard | {isType}</title> </MetaTags>
                <Container fluid>
                    <Breadcrumbs title="Reports" breadcrumbItem={isType} />
                    <Filters callback={handleFilterChange} filterOptions={{
                        order_type: false,
                        city: false,
                        client: true,
                        dates: true,
                        category: false,
                        p_type: false,
                        product: false
                    }} />

                    <Maincard cardItems={counts} loading={isLoading} colSize={3} />

                    <Row>
                        <Col xl="12">
                            <Card>
                                <CardBody>
                                    <Datatable
                                        keyField={'id'}
                                        title={``}
                                        filename={`Orderforms_Report`}
                                        isAdd={false}
                                        handleAddButton=''
                                        isSearch={true}
                                        isExport={true}
                                        columns={tableColumns}
                                        rowsLength={rowData?.total || 0}
                                        rows={rowData?.data || []}
                                        isTableHead={true}
                                        loading={isLoading && (!batchProgress || batchProgress.loaded === 0)}
                                        batchProgress={batchProgress}
                                        onCancelBatch={handleCancelBatch}
                                        batchSize={100}
                                        is_remote={true}
                                        ssr={handlePaginationChange}
                                    />
                                </CardBody>
                            </Card>
                        </Col>
                    </Row>
                </Container>
            </div>
        </React.Fragment>
    )
}

export default Orderforms;
